import {
  Card,
  Button,
  Typography,
  Space,
  Tag,
  Tooltip,
  Modal,
  message,
} from "antd";
import {
  FileTextOutlined,
  DownloadOutlined,
  CreditCardOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { supabase } from "../lib/supabase";

const { Title, Text } = Typography;

interface Invoice {
  id: string;
  invoice_id: string;
  state: string;
  invoice_date: string;
  due_date: string;
  total_price_excl_tax: string;
  total_unpaid: string;
  currency: string;
  url?: string;
  public_view_code?: string;
}

interface BookingInvoiceCardProps {
  bookingId: number;
  companyId: number;
}

const stateColors: Record<string, string> = {
  draft: "default",
  open: "processing",
  scheduled: "warning",
  late: "error",
  reminded: "warning",
  paid: "success",
  uncollectible: "default",
};

export const BookingInvoiceCard: React.FC<BookingInvoiceCardProps> = ({
  bookingId,
  companyId,
}) => {
  const { t } = useTranslation();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [preparingInvoice, setPreparingInvoice] = useState(false);

  const fetchInvoice = async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) return;

      const response = await supabase.functions.invoke(
        "fetch-moneybird-invoices",
        {
          body: { company_id: companyId },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (response.error) {
        console.error("Error fetching invoices:", response.error);
        setLoading(false);
        return;
      }

      // Find invoice that matches this booking reference
      const invoices = response.data.invoices || [];
      const matchingInvoice = invoices.find((inv: Invoice) =>
        inv.reference?.includes(`EFF${bookingId}`)
      );

      setInvoice(matchingInvoice || null);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching invoice:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
  }, [bookingId, companyId]);

  const handleDownloadInvoice = async () => {
    if (!invoice) return;

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("No session");

      const response = await supabase.functions.invoke(
        "download-moneybird-invoice-pdf",
        {
          body: {
            invoice_id: invoice.id,
            company_id: companyId,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (response.data?.pdf_base64) {
        const byteCharacters = atob(response.data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `invoice-${invoice.invoice_id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        message.success(t("invoices.downloadSuccess"));
      } else {
        message.error(t("invoices.downloadError"));
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      message.error(t("invoices.downloadError"));
    }
  };

  const handlePayInvoice = async () => {
    if (!invoice?.url || !invoice?.public_view_code) {
      message.error(t("invoices.noPaymentLink"));
      return;
    }

    try {
      await navigator.clipboard.writeText(invoice.public_view_code);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }

    setPaymentModalVisible(true);
    setPreparingInvoice(true);

    setTimeout(() => {
      window.open(invoice.url, "_blank");
      setPreparingInvoice(false);
      setPaymentModalVisible(false);
    }, 2000);
  };

  const formatCurrency = (amount: string, currency: string) => {
    const numAmount = parseFloat(amount);
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: currency || "EUR",
    }).format(numAmount);
  };

  if (loading || !invoice) {
    return null;
  }

  return (
    <>
      <Card
        title={
          <Space>
            <DollarOutlined />
            {t("invoices.title")}
          </Space>
        }
        style={{ marginBottom: "24px" }}
        extra={
          <Space>
            {invoice.public_view_code && (
              <Tooltip title={t("invoices.clickToCopy")}>
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(invoice.public_view_code!);
                    message.success(t("common.copied"));
                  }}
                  style={{ fontFamily: "monospace", fontWeight: "bold" }}
                >
                  {invoice.public_view_code}
                </Button>
              </Tooltip>
            )}
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            <div>
              <Text type="secondary">{t("invoices.invoiceNumber")}: </Text>
              <Text strong>{invoice.invoice_id}</Text>
            </div>
            <div>
              <Text type="secondary">{t("invoices.status")}: </Text>
              <Tag color={stateColors[invoice.state] || "default"}>
                {invoice.state}
              </Tag>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            <div>
              <Text type="secondary">{t("invoices.invoiceDate")}: </Text>
              <Text>{dayjs(invoice.invoice_date).format("DD-MM-YYYY")}</Text>
            </div>
            <div>
              <Text type="secondary">{t("invoices.dueDate")}: </Text>
              <Text>{dayjs(invoice.due_date).format("DD-MM-YYYY")}</Text>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            <div>
              <Text type="secondary">{t("invoices.totalExclTax")}: </Text>
              <Text strong>
                {formatCurrency(invoice.total_price_excl_tax, invoice.currency)}
              </Text>
            </div>
            <div>
              <Text type="secondary">{t("invoices.unpaid")}: </Text>
              <Text
                strong
                style={{
                  color:
                    parseFloat(invoice.total_unpaid) > 0
                      ? "#ff4d4f"
                      : undefined,
                }}
              >
                {formatCurrency(invoice.total_unpaid, invoice.currency)}
              </Text>
            </div>
          </div>

          <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadInvoice}>
              {t("invoices.downloadInvoice")}
            </Button>
            {invoice.url && (
              <Button
                type="primary"
                icon={<CreditCardOutlined />}
                onClick={handlePayInvoice}
                style={{ background: "#52c41a", borderColor: "#52c41a" }}
              >
                {t("invoices.payInvoice")}
              </Button>
            )}
          </div>
        </Space>
      </Card>

      <Modal
        open={paymentModalVisible}
        footer={null}
        closable={false}
        centered
        width={500}
      >
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          {preparingInvoice ? (
            <>
              <LoadingOutlined
                style={{ fontSize: 48, color: "#1890ff", marginBottom: 24 }}
              />
              <Title level={4}>{t("invoices.preparingInvoice")}</Title>
            </>
          ) : (
            <>
              <CheckCircleOutlined
                style={{ fontSize: 48, color: "#52c41a", marginBottom: 24 }}
              />
              <Title level={4}>{t("invoices.codeCopied")}</Title>
            </>
          )}

          {invoice && (
            <div style={{ marginTop: 24 }}>
              <Typography.Text type="secondary">
                {t("invoices.accessCodeLabel")}
              </Typography.Text>
              <div
                style={{
                  fontSize: 32,
                  fontWeight: "bold",
                  fontFamily: "monospace",
                  margin: "16px 0",
                  padding: "16px",
                  background: "#f5f5f5",
                  borderRadius: 8,
                  letterSpacing: 4,
                }}
              >
                {invoice.public_view_code}
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                <CheckCircleOutlined
                  style={{ color: "#52c41a", marginRight: 8 }}
                />
                {t("invoices.codeCopiedToClipboard")}
              </Typography.Text>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};
